import { Grid, Modal } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import React, { useState } from 'react';
import ReportCreateNewLabelField from '../ReportCreateNewLabelField';
import Dropdown from '@HomaHealth/ui-component-library/lib/_AIQ-components/atoms/Dropdown';
import {
  Button,
  TextField,
  Toogle,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import AutocompleteWithCheckBoxes from '../Autocomplete';
import componentConstants from '../../../core-utils/messages/componentConstants';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { observer } from 'mobx-react';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import { Close } from '@HomaHealth/ui-component-library';
import { scrollBarStyles } from '../../../core-utils/messages/CommonConstants';
import SvgRefresh from '../Icons/Refresh';
// import SvgDelete from '../Icons/SvgDelete';
import { getCohortConditionsByType } from '../../../core-utils/helper-utils/helper';
import { useStores } from '../../../stores';
import { toJS } from 'mobx';

export interface CohortAnalysisModalProps {
  cohortTypes: string[];
}

//common dropdown props
export const commonDropDownProps = {
  selectStyles: {
    height: '36px',
    '& >.MuiOutlinedInput-input': {
      padding: '6px 16px',
      fontSize: '14px',
      lineHeight: '24px',
      color: patientAccessTheme.palette.text.highemphasis,
    },
  },
  labelTop: '10px',
};

const CohortAnalysisModal: React.FC<CohortAnalysisModalProps> = observer(
  ({ cohortTypes }) => {
    const useStyles = makeStyles()({
      root: {
        width: '760px',
        height: '585px',
        backgroundColor: patientAccessTheme.palette.status.white,
        borderRadius: '8px',
        padding: '24px 24px 24px 32px',
        position: 'absolute',
        top: '50%',
        left: '50%',
        marginRight: '-50%',
        transform: 'translate(-50%, -50%)',
        flexDirection: 'column',
      },
      cohortDetailsContainer: {
        overflowY: 'auto',
        ...scrollBarStyles,
      },
      cohortItem: {
        width: '682px',
        height: '170px',
        background: '#FBFBFE',
        borderRadius: '8px',
        marginBottom: '16px',
        padding: '16px 20px',
      },
      cancelButton: {
        padding: '9px 16px',
        width: '102px',
        height: '36px',
        border: '1px solid ' + patientAccessTheme.palette.primary[500],
        borderRadius: '4px',
        textTransform: 'none',
        fontSize: '12px',
        lineHeight: '12px',
        fontWeight: 500,
      },
      saveButton: {
        padding: '9px 16px',
        width: '102px',
        height: '36px',
        border: '1px solid ' + patientAccessTheme.palette.primary[500],
        borderRadius: '4px',
        textTransform: 'none',
        fontSize: '12px',
        lineHeight: '12px',
        fontWeight: 500,
      },
      totalResultsGrid: {
        position: 'absolute',
        right: '60px',
        display: 'flex',
        justifyContent: 'center',
        padding: '10px 26px',
        borderRadius: '8px',
        marginTop: '49px',
        backgroundColor: patientAccessTheme.palette.primary[400],
        width: '300px',
      },
      previewResults: {
        position: 'absolute',
        right: '240px',
      },

      inputRoot: {
        display: 'flex',
        alignItems: 'center',
        borderRadius: '8px',
      },
      inputCohortItem: {
        display: 'flex',
        alignItems: 'center',
      },
      inputFieldsGrid: {
        width: '656px',
        marginRight: '20px',
        background: '#FBFBFE',
        padding: '16px 20px',
        borderRadius: '8px',
      },
      resultsGrid: {
        display: 'flex',
        justifyContent: 'center',
        padding: '10px 26px',
        width: '240px',
        borderRadius: '8px',
        backgroundColor: '#E9EFFF',
      },
      lineAndResultsGrid: {
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'column',
        height: '154px',
      },
      autoCompleteDropdown: {
        '&  > .MuiInputBase-root': {
          height: '36px',
          width: '301px',
          border: 'none',
        },
        '& .MuiAutocomplete-inputRoot[class*="MuiOutlinedInput-root"]': {
          padding: '4px 0px 4px 8px',
        },
        '& .MuiAutocomplete-inputRoot[class*="MuiOutlinedInput-root"] .MuiAutocomplete-input': {
          padding: '0px',
          paddingLeft: '4px',
        },
        '&. MuiAutocomplete-inputRoot[class*="MuiOutlinedInput-root"][class*="MuiOutlinedInput-marginDense"] .MuiAutocomplete-input': {
          padding: '0px',
          paddingLeft: '4px',
        },
      },
      autoCompleteInput: {
        border: 'none',
        backgroundColor: patientAccessTheme.palette.status.white,
        '& .MuiOutlinedInput-notchedOutline': {
          border: `1px solid ${patientAccessTheme.palette.grey[300]}`,
        },
        '& input': {
          fontSize: '14px',
          lineHeight: '24px',
          color: patientAccessTheme.palette.text.mediumemphasis,
          fontWeight: 500,
        },
        '& input::placeholder': {
          fontSize: '14px',
          lineHeight: '24px',
          color: patientAccessTheme.palette.text.mediumemphasis,
          fontWeight: 500,
        },
        '& .MuiAutocomplete-input': {
          padding: '0px',
          height: '27px',
        },
        '&. MuiAutocomplete-inputRoot[class*="MuiOutlinedInput-root"][class*="MuiOutlinedInput-marginDense"] .MuiAutocomplete-input': {
          padding: '0px',
        },
      },
      paper: {
        boxShadow: 'none',
        margin: '0px',
      },
      tag: {
        margin: '3px 8px',
      },
    });

    const cohortDetailsStore = useStores().cohortDetailsStore;

    const [cohortConditions, setCohortConditions] = useState<any[]>(
      getCohortConditionsByType(
        cohortTypes,
        toJS(cohortDetailsStore.cohortValues),
      ),
    );

    const handleRefresh = (index: number, inputType: string) => {
      const localConditions: any = cohortConditions;
      localConditions[index]['propertyValue'] =
        inputType === 'number' ? '' : [];
      setCohortConditions([...localConditions]);
    };

    const handlePropertyChange = (
      index: number,
      storeName: string,
      value: any,
    ) => {
      const localConditions: any = cohortConditions;
      localConditions[index]['propertyValue'] = value;
      setCohortConditions([...localConditions]);
    };

    const checkData = () => {
      let empty: boolean = true;
      cohortConditions.forEach((value: any) => {
        if (
          (Array.isArray(value.propertyValue) &&
            value.propertyValue.length !== 0) ||
          (!Array.isArray(value.propertyValue) && value.propertyValue !== '')
        ) {
          empty = false;
        }
      });
      return empty;
    };

    const handleSaveCohort = () => {
      const obj: any = {};
      cohortConditions.map((value: any) => {
        obj[value.storeName] = value.propertyValue;
      });
      cohortDetailsStore.setCohortValues(obj);
      cohortDetailsStore.toggleCohortModal(false);
    };

    const { classes } = useStyles();
    const constants = componentConstants.ORGANISMS.COHORT_DETAILS;

    return (
      <Modal
        style={{ zIndex: 500 }}
        open={cohortDetailsStore.openCohortModal}
        onClose={() => cohortDetailsStore.toggleCohortModal(false)}
      >
        <Grid container className={classes.root}>
          <Grid
            container
            sx={{
              justifyContent: 'space-between',
            }}
          >
            <Grid>
              <Typography
                fontSize="18px"
                lineHeight="26px"
                fontType="medium"
                textColor={patientAccessTheme.palette.text.highemphasis}
                margin="0px 0px 4px 0px"
              >
                {constants.DETAILS}
              </Typography>
            </Grid>
            <Grid style={{ height: '14px', width: '14px' }}>
              <Close
                height={'14px'}
                width={'14px'}
                fill={patientAccessTheme.palette.status.icon}
                onClick={() => cohortDetailsStore.toggleCohortModal(false)}
                cursor={'pointer'}
              />
            </Grid>
          </Grid>

          <Grid>
            <Typography
              fontSize="14px"
              fontType="regular"
              lineHeight="25px"
              textColor={patientAccessTheme.palette.text.mediumemphasis}
              margin="0px 0px 24px 0px"
            >
              {constants.SUB_TITLE}
            </Typography>
          </Grid>
          <Grid
            className={classes.cohortDetailsContainer}
            style={{
              height: '400px',
            }}
          >
            {cohortConditions.map((cohort: any, index: number) => {
              return (
                <Grid container className={classes.inputRoot} key={index}>
                  <Grid container className={classes.inputCohortItem}>
                    <Grid className={classes.inputFieldsGrid}>
                      <Grid>
                        <ReportCreateNewLabelField
                          label={
                            constants.COHORT_CATEGORY_SELECTION
                              .CATEGORY_DROPDOWN.TITLE
                          }
                          width="100%"
                          component={
                            <Dropdown
                              label={constants.COHORT_DETAILS.SELECT}
                              dropDownOptions={
                                cohort.categoryDropDownOptions ?? []
                              }
                              dropDownValues={
                                cohort.categoryDropDownOptions ?? []
                              }
                              value={cohort.categoryDropDownOptions[0]}
                              setValue={null}
                              selectStyles={{
                                ...commonDropDownProps.selectStyles,
                                width: '230px',
                              }}
                              labelTop="10px"
                              isTooltip={true}
                              tooltipValues={
                                cohort.categoryDropDownOptions ?? []
                              }
                            />
                          }
                        />
                      </Grid>
                      <Grid
                        container
                        sx={{
                          alignItems: 'flex-end',
                        }}
                      >
                        <Grid style={{ marginRight: '20px' }}>
                          <ReportCreateNewLabelField
                            label={constants.COHORT_DETAILS.CONDITION}
                            component={
                              <Dropdown
                                label="Select"
                                dropDownOptions={
                                  cohort.conditionDropDownOptions ?? []
                                }
                                dropDownValues={
                                  cohort.conditionDropDownOptions ?? []
                                }
                                value={cohort.conditionDropDownOptions[0]}
                                setValue={undefined}
                                labelTop="10px"
                                selectStyles={{ width: '230px' }}
                              />
                            }
                          />
                        </Grid>
                        <Grid style={{ marginRight: '20px' }}>
                          {cohort.inputType === 'number' ? (
                            <ReportCreateNewLabelField
                              label={constants.COHORT_DETAILS.PROPERTY}
                              component={
                                <TextField
                                  value={cohort.propertyValue}
                                  placeholder={
                                    constants.COHORT_DETAILS
                                      .TEXT_FIELD_PLACEHOLDER
                                  }
                                  height="36px"
                                  width="300px"
                                  inputPropsStyle={{
                                    paddingLeft: '16px',
                                  }}
                                  onChange={(e: any) => {
                                    handlePropertyChange(
                                      index,
                                      cohort.storeName,
                                      e.target.value,
                                    );
                                  }}
                                />
                              }
                            />
                          ) : (
                            <ReportCreateNewLabelField
                              label={constants.COHORT_DETAILS.PROPERTY}
                              component={
                                <AutocompleteWithCheckBoxes
                                  width="300px"
                                  options={cohort.propertyDropDownOptions}
                                  getOptionLabel={(option) =>
                                    option.toString() ?? ''
                                  }
                                  handleChange={(e, value) => {
                                    handlePropertyChange(
                                      index,
                                      cohort.storeName,
                                      value,
                                    );
                                  }}
                                  defaultValue={cohort.propertyValue ?? []}
                                  placeholder={constants.COHORT_DETAILS.SELECT}
                                  popupIcon={<ArrowDropDownIcon />}
                                />
                              }
                            />
                          )}
                        </Grid>
                        <Grid>
                          <SvgRefresh
                            height={'24px'}
                            width={'24px'}
                            style={{ marginBottom: '16px' }}
                            cursor={'pointer'}
                            onClick={() =>
                              handleRefresh(index, cohort.inputType)
                            }
                          />
                          {/* <SvgDelete
                            height={'24px'}
                            width={'24px'}
                            style={{ marginBottom: '16px', marginLeft: '8px' }}
                            cursor={'pointer'}
                            onClick={() => {}}
                          /> */}
                        </Grid>
                      </Grid>
                    </Grid>

                    <Grid style={{ margin: '16px 0px' }}>
                      {cohortConditions?.length > 1 &&
                        index !== cohortConditions?.length - 1 && (
                          <Grid>
                            <Toogle
                              value={'AND'}
                              title={'AND'}
                              showLabel={true}
                              icon={''}
                              toggleStyles={{
                                color: 'white',
                                background:
                                  patientAccessTheme.palette.primary[500],
                                padding: '4px 14px 4px 6px',
                              }}
                            />
                          </Grid>
                        )}
                    </Grid>
                  </Grid>
                </Grid>
              );
            })}
          </Grid>
          <Grid
            container
            style={{
              position: 'absolute',
              bottom: 0,
              margin: '0px 0px 20px 400px',
            }}
            sx={{
              alignItems: 'center',
            }}
          >
            <Grid style={{ marginRight: '12px' }}>
              <Button
                label={constants.CANCEL}
                variant="outlined"
                className={classes.cancelButton}
                onClick={() => cohortDetailsStore.toggleCohortModal(false)}
              />
            </Grid>
            <Grid>
              <Button
                label={constants.SAVE}
                variant="contained"
                className={classes.saveButton}
                disabled={checkData()}
                onClick={() => handleSaveCohort()}
              />
            </Grid>
          </Grid>
        </Grid>
      </Modal>
    );
  },
);

export default CohortAnalysisModal;
